import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { api } from '../../utils/apiClient';
import { Transaction, TransactionStatus } from '../../utils/types'; // Import from shared types
import { usePagination } from '../../hooks/usePagination';
import Pagination from '../common/Pagination';
import { config } from '../../config';
import TransactionFilters, { TimeframeOption } from '../common/TransactionFilters';
import { useMetaTags } from '../../hooks/useMetaTags';
import { emailConfig } from '../../config/emailConfig';

/**
 * PurchaseHistory component displays a user's token purchase history
 */
function PurchaseHistory() {
  const { t } = useTranslation();
  const { user } = useUser();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeframe, setTimeframe] = useState<TimeframeOption>('all');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Use the pagination hook
  const { 
    currentPage, 
    paginate, 
    totalPages,
    itemsPerPage,
    itemsPerPageOptions,
    handleItemsPerPageChange,
    getCurrentItems
  } = usePagination({
    totalItems: transactions.length,
    defaultItemsPerPage: config.pagination.defaultItemsPerPage
  });

  // Get current page transactions
  const { currentItems: paginatedTransactions } = getCurrentItems(transactions);

  // Fetch transaction history
  const fetchTransactions = useCallback(async () => {
    if (!user.email) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    setIsRefreshing(true);

    try {
      // Call the API to get transaction history (no email needed)
      const response = await api.wallet.getTransactionHistory(timeframe);
      
      if (response.success && response.transactions) {
        // Convert string dates to Date objects
        const formattedTransactions: Transaction[] = response.transactions.map(tx => ({
          id: tx.id,
          date: new Date(tx.date),
          tokenAmount: tx.tokenAmount,
          price: tx.price,
          status: tx.status as TransactionStatus,
          paymentMethod: tx.paymentMethod,
          failureReason: tx.failureReason
        }));
        
        setTransactions(formattedTransactions);
      } else {
        // Show error message if API call was not successful
        setError(response.message || t('errors.failedToFetchHistory', 'Failed to fetch purchase history'));
        setTransactions([]);
      }
    } catch (err) {
      console.error('Error fetching transaction history:', err);
      setError(t('errors.failedToFetchHistory', 'Failed to fetch purchase history. Please try again later.'));
      setTransactions([]);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [timeframe, t]);

  // Initial load
  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Filter transactions based on selected timeframe
  const filterTransactionsByTimeframe = (transactions: Transaction[], timeframe: string) => {
    if (timeframe === 'all') return transactions;
    
    const now = new Date();
    let cutoffDate: Date;
    
    if (timeframe === 'today') {
      cutoffDate = new Date(now.setHours(0, 0, 0, 0));
    } else { // 7days
      cutoffDate = new Date(now);
      cutoffDate.setDate(cutoffDate.getDate() - 7);
    }
    
    return transactions.filter(tx => tx.date >= cutoffDate);
  };

  // Handle timeframe change
  const handleTimeframeChange = (newTimeframe: TimeframeOption) => {
    setTimeframe(newTimeframe);
  };

  // Initialize tooltips
  useEffect(() => {
    // Check if Bootstrap is available
    if (typeof window !== 'undefined' && window.bootstrap?.Tooltip) {
      // Initialize all tooltips
      const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
      const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => 
        new window.bootstrap.Tooltip(tooltipTriggerEl)
      );
      
      // Cleanup function to dispose tooltips
      return () => {
        tooltipList.forEach(tooltip => {
          if (tooltip && typeof tooltip.dispose === 'function') {
            tooltip.dispose();
          }
        });
      };
    }
  }, [transactions]); // Re-run when transactions change

  return (
    <div className="container py-4">
      <div className="row justify-content-center">
        <div className="col-lg-10">
          {/* Header with back button */}
          <div className="d-flex align-items-center mb-4">
            <Link to="/wallet" className="btn btn-outline-secondary me-3">
              <i className="bi bi-arrow-left"></i>
            </Link>
            <h1 className="h3 mb-0">{t('history.purchaseHistory', 'Purchase History')}</h1>
          </div>

          {/* Filter controls using shared component */}
          <TransactionFilters
            timeframe={timeframe}
            onTimeframeChange={handleTimeframeChange}
            onRefresh={fetchTransactions}
            isRefreshing={isRefreshing}
          />

          {/* Main content */}
          <div className="border rounded">
            {isLoading ? (
              <div className="text-center p-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">{t('common.loading', 'Loading...')}</span>
                </div>
                <p className="mt-3 text-muted">{t('history.loadingTransactions', 'Loading your purchase history...')}</p>
              </div>
            ) : error ? (
              <div className="alert alert-danger m-4" role="alert">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                {error}
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center p-5">
                <i className="bi bi-receipt text-muted" style={{ fontSize: '3rem' }}></i>
                <h5 className="mt-3">{t('history.noTransactions', 'No Purchase History')}</h5>
                <p className="text-muted">{t('history.noTransactionsDesc', 'You haven\'t purchased any tokens yet.')}</p>
                <Link to="/wallet" className="btn btn-primary mt-2">
                  <i className="bi bi-wallet2 me-2"></i>
                  {t('history.buyTokens', 'Buy Tokens')}
                </Link>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th className="border-0">{t('history.date', 'Date')}</th>
                      <th className="border-0">{t('history.paymentId', 'Payment ID')}</th>
                      <th className="border-0">{t('history.tokens', 'Tokens')}</th>
                      <th className="border-0">{t('history.amount', 'Amount')}</th>
                      <th className="border-0">{t('history.status', 'Status')}</th>
                      <th className="border-0">{t('history.paymentMethod', 'Payment Method')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTransactions.map((transaction) => (
                      <tr key={transaction.id}>
                        <td>
                          {transaction.date instanceof Date 
                            ? (
                              <>
                                {transaction.date.toLocaleDateString()}{' '}
                                <span className="text-muted small">
                                  {transaction.date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </>
                            ) 
                            : typeof transaction.date === 'string' 
                              ? new Date(transaction.date).toLocaleString()
                              : 'Invalid date'
                        }
                        </td>
                        <td>
                          <span className="d-inline-block">
                            {transaction.id}
                          </span>
                        </td>
                        <td className="fw-medium">
                          +{transaction.tokenAmount.toLocaleString()}
                        </td>
                        <td>€{transaction.price.toFixed(2)}</td>
                        <td>
                          {transaction.status === 'completed' && (
                            <span className="badge bg-success">
                              {t('history.completed', 'Completed')}
                            </span>
                          )}
                          {transaction.status === 'pending' && (
                            <span className="badge bg-warning text-dark">
                              {t('history.pending', 'Pending')}
                            </span>
                          )}
                          {transaction.status === 'failed' && (
                            <div>
                              <span className="badge bg-danger mb-1">
                                {t('history.failed', 'Failed')}
                              </span>
                              {transaction.failureReason && (
                                <div className="small text-danger">
                                  {transaction.failureReason.includes('authentication') || transaction.failureReason.includes('auth')
                                    ? t('history.authFailed', 'Card authentication failed')
                                    : transaction.failureReason.includes('declined')
                                      ? t('history.cardDeclined', 'Card was declined')
                                      : t('history.paymentFailed', 'Payment failed')}
                                  <i className="bi bi-info-circle ms-1" 
                                    title={transaction.failureReason}
                                    data-bs-toggle="tooltip" 
                                    data-bs-placement="top"></i>
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td>{transaction.paymentMethod || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Pagination */}
            {transactions.length > 0 && (
              <Pagination 
                currentPage={currentPage}
                totalPages={totalPages}
                paginate={paginate}
                itemsPerPage={itemsPerPage}
                totalItems={transactions.length}
                itemsPerPageOptions={itemsPerPageOptions}
                handleItemsPerPageChange={handleItemsPerPageChange}
                ariaLabel={t('pagination.purchaseHistoryPagination', 'Purchase history pagination')}
              />
            )}
          </div>

          {/* Summary card */}
          {transactions.length > 0 && (
            <div className="border-top pt-4 mt-4">
              <div className="row">
                <div className="col-md-4 mb-3 mb-md-0">
                  <div className="d-flex align-items-center">
                    <div className="text-primary p-2 me-3">
                      <i className="bi bi-coin fs-4"></i>
                    </div>
                    <div>
                      <h6 className="mb-0 text-muted">{t('history.totalTokens', 'Total Tokens')}</h6>
                      <p className="mb-0 fs-5 fw-bold">
                        {transactions
                          .filter(tx => tx.status === 'completed')
                          .reduce((sum, tx) => sum + tx.tokenAmount, 0)
                          .toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-4 mb-3 mb-md-0">
                  <div className="d-flex align-items-center">
                    <div className="text-success p-2 me-3">
                      <i className="bi bi-credit-card fs-4"></i>
                    </div>
                    <div>
                      <h6 className="mb-0 text-muted">{t('history.totalSpent', 'Total Spent')}</h6>
                      <p className="mb-0 fs-5 fw-bold">
                        €{transactions
                          .filter(tx => tx.status === 'completed')
                          .reduce((sum, tx) => sum + tx.price, 0)
                          .toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="d-flex align-items-center">
                    <div className="text-info p-2 me-3">
                      <i className="bi bi-receipt fs-4"></i>
                    </div>
                    <div>
                      <h6 className="mb-0 text-muted">{t('history.transactions', 'Transactions')}</h6>
                      <p className="mb-0 fs-5 fw-bold">
                        {transactions.length}
                        {transactions.some(tx => tx.status === 'failed') && (
                          <span className="ms-2 fs-6 text-danger">
                            ({transactions.filter(tx => tx.status === 'failed').length} {t('history.failed', 'failed')})
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Help section */}
          <div className="mt-4 text-center text-md-end">
            <small className="text-muted">
              {t('history.needHelp', 'Need help with your purchases?')}{' '}
              <a href={`mailto:${emailConfig.supportEmail}`} className="text-decoration-none">
                {t('history.contactSupport', 'Contact support')}
              </a>
            </small>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PurchaseHistory; 